/**
 * RD & Company CRM - Warranty Request (Client View) Layout
 */

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

// Authentication context
import { useAuth } from "contexts/AuthContext";

// API Configuration
import { API_CONFIG, getApiUrl } from "config/api";

function WarrantyRequest() {
  const { user, token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debug authentication
  console.log("WarrantyRequest - User:", user);
  console.log("WarrantyRequest - Token:", token ? "Present" : "Missing");
  console.log("WarrantyRequest - User role:", user?.role);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [expandedRequests, setExpandedRequests] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    product_name: "",
    product_description: "",
    serial_number: "",
    purchase_date: "",
    purchase_price: "",
    issue_description: "",
    request_type: "warranty",
    priority: "medium",
  });

  const toggleRequestExpansion = (requestId) => {
    setExpandedRequests((prev) => ({
      ...prev,
      [requestId]: !prev[requestId],
    }));
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      console.log("Fetching warranty requests...");
      console.log("API URL:", `${getApiUrl(API_CONFIG.ENDPOINTS.WARRANTY)}`);
      console.log("Token available:", !!token);

      const response = await fetch(`${getApiUrl(API_CONFIG.ENDPOINTS.WARRANTY)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Response status:", response.status);
      console.log("Response ok:", response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response:", errorText);
        throw new Error(
          `Failed to fetch warranty requests: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log("Fetched data:", data);
      setRequests(data.requests || []);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if user is authenticated and has client role
    if (!user || !token) {
      setError("Please log in to access warranty requests.");
      setLoading(false);
      return;
    }

    if (user.role !== "client") {
      setError("Access denied. Only clients can access warranty requests.");
      setLoading(false);
      return;
    }

    fetchRequests();
  }, [user, token]);

  const openAddDialog = () => {
    setIsAddOpen(true);
    setValidationErrors({});
    setError(null);
  };

  const closeAddDialog = () => {
    setIsAddOpen(false);
    setFormData({
      product_name: "",
      product_description: "",
      serial_number: "",
      purchase_date: "",
      purchase_price: "",
      issue_description: "",
      request_type: "warranty",
      priority: "medium",
    });
    setValidationErrors({});
    setError(null);
  };

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    // Clear validation error for this field when user starts typing
    if (validationErrors[key]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.product_name.trim()) {
      errors.product_name = "Product name is required";
    }

    if (!formData.issue_description.trim()) {
      errors.issue_description = "Issue description is required";
    }

    if (
      formData.purchase_price &&
      (isNaN(formData.purchase_price) || formData.purchase_price < 0)
    ) {
      errors.purchase_price = "Purchase price must be a valid positive number";
    }

    if (formData.purchase_date && new Date(formData.purchase_date) > new Date()) {
      errors.purchase_date = "Purchase date cannot be in the future";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitRequest = async () => {
    if (!validateForm()) {
      return;
    }

    // Check if user is authenticated and has client role
    if (!user || !token) {
      setError("Please log in to create warranty requests.");
      return;
    }

    if (user.role !== "client") {
      setError("Access denied. Only clients can create warranty requests.");
      return;
    }

    // Check if token is available
    if (!token) {
      setError("Authentication token not found. Please log in again.");
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("Submitting warranty request:", formData);
      console.log("API URL:", `${getApiUrl(API_CONFIG.ENDPOINTS.WARRANTY)}`);
      console.log("Token available:", !!token);

      const response = await fetch(`${getApiUrl(API_CONFIG.ENDPOINTS.WARRANTY)}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);

      const data = await response.json();
      console.log("Response data:", data);

      if (data.success) {
        closeAddDialog();
        await fetchRequests();
      } else {
        setError(data.message || "Failed to create warranty request");
        if (data.errors) {
          setValidationErrors(data.errors);
        }
      }
    } catch (err) {
      console.error("Network error details:", err);
      setError(`Network error while creating warranty request: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this warranty request?")) return;

    try {
      const response = await fetch(`${getApiUrl(API_CONFIG.ENDPOINTS.WARRANTY)}/${requestId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await fetchRequests();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.message || "Failed to delete warranty request");
      }
    } catch (err) {
      alert("Network error while deleting warranty request");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "under_review":
        return "info";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "completed":
        return "success";
      default:
        return "secondary";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "low":
        return "success";
      case "medium":
        return "warning";
      case "high":
        return "error";
      case "urgent":
        return "error";
      default:
        return "secondary";
    }
  };

  const filteredRequests = requests.filter((request) => {
    const matchesSearch =
      request.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.issue_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.request_type.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || request.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const columns = [
    { Header: "Product", accessor: "product", width: "20%" },
    { Header: "Type", accessor: "request_type", width: "10%" },
    { Header: "Priority", accessor: "priority", width: "10%" },
    { Header: "Status", accessor: "status", width: "12%" },
    { Header: "Date", accessor: "created_at", width: "12%" },
    { Header: "Actions", accessor: "actions", width: "8%", disableSortBy: true },
  ];

  const rows = filteredRequests.map((request) => ({
    product: (
      <Box>
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {request.product_name}
        </MDTypography>
        {request.serial_number && (
          <MDTypography variant="caption" color="text.secondary" display="block">
            SN: {request.serial_number}
          </MDTypography>
        )}
      </Box>
    ),
    request_type: (
      <Chip
        label={request.request_type.charAt(0).toUpperCase() + request.request_type.slice(1)}
        color="info"
        size="small"
      />
    ),
    priority: (
      <Chip
        label={request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
        color={getPriorityColor(request.priority)}
        size="small"
      />
    ),
    status: (
      <Chip
        label={
          request.status.replace("_", " ").charAt(0).toUpperCase() +
          request.status.replace("_", " ").slice(1)
        }
        color={getStatusColor(request.status)}
        size="small"
      />
    ),
    created_at: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {new Date(request.created_at).toLocaleDateString()}
      </MDTypography>
    ),
    actions: (
      <MDBox display="flex" alignItems="center">
        <MDButton
          variant="text"
          color="info"
          size="small"
          onClick={() => toggleRequestExpansion(request.id)}
        >
          <Icon>{expandedRequests[request.id] ? "expand_less" : "expand_more"}</Icon>
        </MDButton>
        {request.status === "pending" && (
          <MDButton
            variant="text"
            color="error"
            size="small"
            onClick={() => handleDeleteRequest(request.id)}
          >
            <Icon>delete</Icon>
          </MDButton>
        )}
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Card>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Warranty Requests
                </MDTypography>
                <MDButton variant="gradient" color="info" size="small" onClick={openAddDialog}>
                  <Icon>add</Icon>&nbsp;New Request
                </MDButton>
              </MDBox>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <MDInput
                    type="text"
                    placeholder="Search requests by product name, issue, or type..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDInput
                    select
                    label="Filter by Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="under_review">Under Review</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </MDInput>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </MDBox>

        <Card>
          <MDBox pt={3} px={3}>
            <MDTypography variant="h6" fontWeight="medium">
              My Warranty Requests
            </MDTypography>
          </MDBox>
          <MDBox pt={1} pb={2} px={3}>
            {loading ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <MDTypography variant="body2" color="text">
                  Loading warranty requests...
                </MDTypography>
              </MDBox>
            ) : error ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <MDTypography variant="body2" color="error">
                  {error}
                </MDTypography>
                <MDButton variant="gradient" color="info" onClick={fetchRequests} sx={{ mt: 2 }}>
                  Retry
                </MDButton>
              </MDBox>
            ) : (
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
              />
            )}
          </MDBox>
        </Card>

        {/* Expanded Request Details */}
        {filteredRequests.map((request) => (
          <Collapse key={`details-${request.id}`} in={expandedRequests[request.id]}>
            <Card sx={{ mt: 2 }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Request Details
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="body2" color="text.secondary">
                      <strong>Product Name:</strong> {request.product_name}
                    </MDTypography>
                    {request.product_description && (
                      <MDTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        <strong>Description:</strong> {request.product_description}
                      </MDTypography>
                    )}
                    {request.serial_number && (
                      <MDTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        <strong>Serial Number:</strong> {request.serial_number}
                      </MDTypography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {request.purchase_date && (
                      <MDTypography variant="body2" color="text.secondary">
                        <strong>Purchase Date:</strong>{" "}
                        {new Date(request.purchase_date).toLocaleDateString()}
                      </MDTypography>
                    )}
                    {request.purchase_price && (
                      <MDTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        <strong>Purchase Price:</strong> ₹{request.purchase_price}
                      </MDTypography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <MDTypography variant="body2" color="text.secondary">
                      <strong>Issue Description:</strong>
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      color="text"
                      sx={{ mt: 1, p: 2, bgcolor: "grey.50", borderRadius: 1 }}
                    >
                      {request.issue_description}
                    </MDTypography>
                  </Grid>
                  {request.admin_notes && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <MDTypography variant="body2" color="text.secondary">
                        <strong>Admin Notes:</strong>
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        color="text"
                        sx={{ mt: 1, p: 2, bgcolor: "info.light", borderRadius: 1 }}
                      >
                        {request.admin_notes}
                      </MDTypography>
                    </Grid>
                  )}
                  {request.resolution_notes && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <MDTypography variant="body2" color="text.secondary">
                        <strong>Resolution Notes:</strong>
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        color="text"
                        sx={{ mt: 1, p: 2, bgcolor: "success.light", borderRadius: 1 }}
                      >
                        {request.resolution_notes}
                      </MDTypography>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Collapse>
        ))}
      </MDBox>

      {/* Add Warranty Request Dialog */}
      <Dialog open={isAddOpen} onClose={closeAddDialog} fullWidth maxWidth="md">
        <DialogTitle>Create New Warranty Request</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            {/* Display general error message */}
            {error && (
              <MDBox mb={2}>
                <MDTypography variant="body2" color="error">
                  {error}
                </MDTypography>
              </MDBox>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Product Name *"
                    value={formData.product_name}
                    onChange={(e) => updateField("product_name", e.target.value)}
                    fullWidth
                    error={!!validationErrors.product_name}
                    success={!validationErrors.product_name && formData.product_name.length > 0}
                  />
                  {validationErrors.product_name && (
                    <MDTypography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {validationErrors.product_name}
                    </MDTypography>
                  )}
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Serial Number"
                    value={formData.serial_number}
                    onChange={(e) => updateField("serial_number", e.target.value)}
                    fullWidth
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Product Description"
                    value={formData.product_description}
                    onChange={(e) => updateField("product_description", e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDInput
                    type="date"
                    label="Purchase Date"
                    value={formData.purchase_date}
                    onChange={(e) => updateField("purchase_date", e.target.value)}
                    fullWidth
                    error={!!validationErrors.purchase_date}
                    InputLabelProps={{ shrink: true }}
                  />
                  {validationErrors.purchase_date && (
                    <MDTypography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {validationErrors.purchase_date}
                    </MDTypography>
                  )}
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDInput
                    type="number"
                    label="Purchase Price (₹)"
                    value={formData.purchase_price}
                    onChange={(e) => updateField("purchase_price", e.target.value)}
                    fullWidth
                    error={!!validationErrors.purchase_price}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                  {validationErrors.purchase_price && (
                    <MDTypography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {validationErrors.purchase_price}
                    </MDTypography>
                  )}
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDInput
                    select
                    label="Request Type *"
                    value={formData.request_type}
                    onChange={(e) => updateField("request_type", e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="warranty">Warranty</MenuItem>
                    <MenuItem value="replacement">Replacement</MenuItem>
                    <MenuItem value="repair">Repair</MenuItem>
                  </MDInput>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDInput
                    select
                    label="Priority"
                    value={formData.priority}
                    onChange={(e) => updateField("priority", e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </MDInput>
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Issue Description *"
                    value={formData.issue_description}
                    onChange={(e) => updateField("issue_description", e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                    error={!!validationErrors.issue_description}
                    success={
                      !validationErrors.issue_description && formData.issue_description.length > 0
                    }
                  />
                  {validationErrors.issue_description && (
                    <MDTypography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {validationErrors.issue_description}
                    </MDTypography>
                  )}
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={closeAddDialog} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleSubmitRequest} color="info" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Request"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default WarrantyRequest;
